import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { useGetSsoAuthUrl } from 'utils/customHooks/api/useGetSsoAuthUrl';
import { getWindowLocation } from 'utils/window';
import { DynamicHelium } from 'utils/helium';
import { useOpMutation } from 'utils/customHooks/useOpMutation';

import { useAltaLoginUrlParams } from '../../hooks/useAltaLoginUrlParams';
import { LoginFlow } from '../LoginFlow/LoginFlow';
import { ForgotPasswordFlow } from '../ForgotPasswordFlow/ForgotPasswordFlow';
import { ResetPasswordFlow } from '../ResetPasswordFlow/ResetPasswordFlow';
import { UseAltaIdButton } from '../UseAltaIdButton/UseAltaIdButton';

const PROD_API = 'https://helium.prod.openpath.com';
const PRODEU1_API = 'https://helium.prodeu1.openpath.com';
const DEV_API = 'https://helium.dev.openpath.com';
const SANDBOX_API = 'https://helium.sandbox.openpath.com';

interface UnifiedLoginProps {
  redirectUrl?: string;
}

export interface HandleIdpSsoLoginArgs {
  email: string;
  namespaceNickname: string;
  env?: OP.Env;
  onError?: (error: unknown) => void;
}

export const UnifiedLogin = ({ redirectUrl }: UnifiedLoginProps) => {
  const { t } = useTranslation();

  const { resetPassword } = useAltaLoginUrlParams();
  const [forgotPassword, setForgotPassword] = useState<{
    didForget: boolean;
    initialEmail: string | null;
  }>({
    didForget: false,
    initialEmail: null,
  });

  const prodHeliumClient = new DynamicHelium(PROD_API).createClient();
  const prodeu1HeliumClient = new DynamicHelium(PRODEU1_API).createClient();
  const devHeliumClient = new DynamicHelium(DEV_API).createClient();
  const sandboxHeliumClient = new DynamicHelium(SANDBOX_API).createClient();

  const getSsoAuthUrlMutation = useGetSsoAuthUrl();

  const prodSsoAuthUrlMutation = useOpMutation({
    apiEndpointName: 'getSsoAuthUrl',
    shouldSuppressErrorMessage: (error) =>
      error.statusCode === 401 && error.message === 'Namespace is required.',
    overrideApiClient: prodHeliumClient,
  });
  const prodeu1SsoAuthUrlMutation = useOpMutation({
    apiEndpointName: 'getSsoAuthUrl',
    shouldSuppressErrorMessage: (error) =>
      error.statusCode === 401 && error.message === 'Namespace is required.',
    overrideApiClient: prodeu1HeliumClient,
  });
  const devSsoAuthUrlMutation = useOpMutation({
    apiEndpointName: 'getSsoAuthUrl',
    shouldSuppressErrorMessage: (error) =>
      error.statusCode === 401 && error.message === 'Namespace is required.',
    overrideApiClient: devHeliumClient,
  });

  const sandboxSsoAuthUrlMutation = useOpMutation({
    apiEndpointName: 'getSsoAuthUrl',
    shouldSuppressErrorMessage: (error) =>
      error.statusCode === 401 && error.message === 'Namespace is required.',
    overrideApiClient: sandboxHeliumClient,
  });

  const envSsoMutations = useMemo(
    () => ({
      dev: devSsoAuthUrlMutation,
      prod: prodSsoAuthUrlMutation,
      prodeu1: prodeu1SsoAuthUrlMutation,
      sandbox: sandboxSsoAuthUrlMutation,
      mine: getSsoAuthUrlMutation,
      test: getSsoAuthUrlMutation,
    }),
    [
      devSsoAuthUrlMutation,
      prodSsoAuthUrlMutation,
      prodeu1SsoAuthUrlMutation,
      sandboxSsoAuthUrlMutation,
      getSsoAuthUrlMutation,
    ],
  );

  // call a SSO login api for IDP
  const handleIdpSsoLogin = ({
    email,
    env,
    namespaceNickname,
    onError,
  }: HandleIdpSsoLoginArgs) => {
    // have a fallback which relies on current env variable for whatever reason which will generally be "mine" and "test"
    const mutation = env ? envSsoMutations[env] : getSsoAuthUrlMutation;

    mutation.mutate(
      {
        payload: {
          email,
          namespaceNickname,
          ...(redirectUrl && { redirectUrl }),
        },
      },
      {
        onSuccess: (data) => {
          getWindowLocation().href = data.authUrl!;
        },
        onError: (error) => {
          if (typeof onError === 'function') {
            onError(error);
          }
        },
      },
    );
  };

  const handleBackToSignIn = useCallback((initialEmail: string) => {
    setForgotPassword({ didForget: false, initialEmail });
  }, []);

  const showLogin = !resetPassword && !forgotPassword.didForget;
  const showForgotPassword = !resetPassword && forgotPassword.didForget;
  let FormInputPortion = null;

  if (showLogin) {
    FormInputPortion = (
      <LoginFlow
        initialEmail={forgotPassword.initialEmail}
        onForgotPassword={setForgotPassword}
        handleIdpSsoLogin={handleIdpSsoLogin}
      />
    );
  }

  if (showForgotPassword) {
    FormInputPortion = (
      <ForgotPasswordFlow
        initialEmail={forgotPassword.initialEmail}
        onBackToSignIn={handleBackToSignIn}
        handleIdpSsoLogin={handleIdpSsoLogin}
      />
    );
  }

  if (resetPassword) {
    FormInputPortion = <ResetPasswordFlow backToSignin={handleBackToSignIn} />;
  }

  return (
    <>
      {FormInputPortion}

      <OpDivider plain>{t('or')}</OpDivider>
      <UseAltaIdButton />
    </>
  );
};
